import type { ApiErrorCode, ApiErrorDto } from '@gty/common';

/**
 * A typed failure from the API. Carries the server's `ApiErrorDto.code` when
 * one was returned, because the refresh interceptor branches on it
 * (`TOKEN_EXPIRED` -> try refresh, `FORCE_REAUTH` -> give up immediately).
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode | null;
  readonly body: ApiErrorDto | null;

  constructor(status: number, message: string, code: ApiErrorCode | null, body: ApiErrorDto | null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/**
 * Must stay in step with `ApiErrorCode` in `@gty/common` AND with `KNOWN_CODES`
 * in the server's `all-exceptions.filter.ts`.
 *
 * A code missing HERE is not rejected — it is parsed as `null`, so a screen
 * branching on `err.code` silently falls through to its generic message. The
 * server had exactly this bug in the other direction: DUPLICATE_REQUEST was
 * being flattened to INTERNAL_ERROR before the filter learned about it.
 */
const API_ERROR_CODES: readonly string[] = [
  'INVALID_CREDENTIALS',
  'TOKEN_EXPIRED',
  'FORCE_REAUTH',
  'PERMISSION_DENIED',
  'UNGUARDED_ROUTE',
  'OWNER_ONLY',
  'VALIDATION_FAILED',
  'DUPLICATE_REQUEST',
  'DOCUMENT_LOCKED',
  'DOCUMENT_CONFLICT',
  'BOOKING_VALUE_CHANGE_REQUIRED',
  'NOT_FOUND',
  'PAYLOAD_TOO_LARGE',
  'MEDIA_STORAGE_NOT_CONFIGURED',
  'INTERNAL_ERROR',
];

function isApiErrorCode(value: unknown): value is ApiErrorCode {
  return typeof value === 'string' && API_ERROR_CODES.includes(value);
}

/**
 * Parse an error response body into an `ApiError`.
 *
 * Deliberately total: a proxy, a gateway or an Nginx error page will not return
 * `ApiErrorDto`-shaped JSON, and that must produce a normal `ApiError` rather
 * than a `TypeError` from destructuring `undefined`.
 */
export async function toApiError(response: Response): Promise<ApiError> {
  let raw: unknown = null;
  try {
    const text = await response.text();
    raw = text.length > 0 ? (JSON.parse(text) as unknown) : null;
  } catch {
    raw = null;
  }

  const record = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : null;
  const code = isApiErrorCode(record?.['code']) ? record['code'] : null;
  const message =
    typeof record?.['message'] === 'string' ? record['message'] : `Request failed (${response.status})`;

  return new ApiError(response.status, message, code, record as ApiErrorDto | null);
}

/**
 * The server's correlation id for a 5xx, when it sent one.
 *
 * Set by the exception filter on 5xx ONLY, and it is the single string that ties
 * what the user saw to the stack the server logged. A user reporting "it said
 * something went wrong" is undiagnosable; a user quoting this is one log grep,
 * so any screen that can show a 500 must show it.
 */
export function correlationIdOf(error: unknown): string | null {
  if (!isApiError(error)) return null;
  const id = error.body?.correlationId;
  return typeof id === 'string' && id.length > 0 ? id : null;
}
